import {
    FaHashtag,
    FaUser,
    FaEnvelope,
    FaCalendarAlt,
    FaRegCommentDots,
    Header,
    OptionHelper,
    RecordMap
} from "@/Shared";

import { BirthWishViewModel } from ".";
import { ContactModel } from "@/Domains/Contact";
import { UserModel } from "@/Domains/User";


/**
 * =====================================================
 * Class: BirthWishHeader
 * =====================================================
 *
 * Description:
 * Provides column configuration for the Birthday Wishes table view.
 * Each column includes metadata such as key, label, icon, type, sorting, and filtering options.
 *
 * Responsibilities:
 * - Define all table headers for `BirthWishViewModel`
 * - Provide sortable and filterable configurations
 * - Supply icons for better UI representation
 * - Generate a full table configuration through the static `build` method
 *
 * Notes:
 * - All columns are strongly typed based on `BirthWishViewModel`
 * - Filtering is case-insensitive where applicable
 *
 * Author: Samano CASTRE
 * Date: February 1, 2026
 */
export class BirthWishHeader {

    /** Private constructor to prevent instantiation from outside */
    private constructor() { }

    /** =====================================================
     * Columns
     * =====================================================
     */

    /** Unique identifier of the birthday wish */
    get id() {
        return {
            key: 'id',
            icon: <FaHashtag />,
            label: 'ID',
            type: 'number',
            isSortable: true
        };
    }

    /** Name of the contact */
    getContact( contactMap : RecordMap<ContactModel>) {
        return {
            key: 'contact',
            icon: <FaUser />,
            label: 'Contact',
            type: 'string',
            isSortable: true,
            filter: {
                label: 'Contact',
                case: 'insensitive',
                options : OptionHelper.contactOptions(contactMap.toArray())
            }
        };
    }

    /** Email address of the contact */
    get contactEmail() {
        return {
            key: 'contactEmail',
            icon: <FaEnvelope />,
            label: 'E-mail du contact',
            type: 'string',
            isSortable: true,
            filter: {
                label: 'E-mail',
                case: 'insensitive'
            }
        };
    }

    /** Birthday date of the contact */
    get birthday() {
        return {
            key: 'birthday',
            icon: <FaCalendarAlt />,
            label: 'Anniversaire',
            type: 'date',
            isSortable: true
        };
    }

    /** Year when the message was sent */
    get mailingYear() {
        return {
            key: 'mailingYear',
            icon: <FaCalendarAlt />,
            label: 'Année d’envoi',
            type: 'number',
            isSortable: true
        };
    }

    /** Message content entered by the agent */
    get message() {
        return {
            key: 'message',
            icon: <FaRegCommentDots />,
            label: 'Message',
            type: 'string',
            isSortable: false
        };
    }

    /** Date when the message was created/sent */
    get creationDate() {
        return {
            key: 'creationDate',
            icon: <FaCalendarAlt />,
            label: 'Date d’envoi',
            type: 'date',
            isSortable: true
        };
    }

    /** =====================================================
    * Static Builder
    * =====================================================
    *
    * Constructs the complete table configuration for `BirthWishViewModel`.
    * Only includes the relevant columns in the table view.
    */
    static build(
        contactMap : RecordMap<ContactModel>, 
    ): Record<keyof BirthWishViewModel, Header<BirthWishViewModel>> {
        
        const header = new BirthWishHeader();

        return {
            id: header.id,
            contact: header.getContact(contactMap),
            birthday: header.birthday,
            creationDate: header.creationDate,
            message: header.message,
        } as Record<keyof BirthWishViewModel, Header<BirthWishViewModel>>;
    }
}
